import React, { useState } from "react";
import { Box, Text, Card, Icon, Flex } from "@chakra-ui/react";
import { FiInfo } from 'react-icons/fi';

import { groupAlerts } from '../../utils/forecasts';
import useForecasts from "../../components/customHooks/useForecasts";
import { RegionFilterDrawer } from "../../components/regionFilterDrawer/RegionFilterDrawer";
import { SelectedRegionBar } from "../../components/regionFilterDrawer/SelectedRegionBar";
import useRegions from "../../components/customHooks/useRegions";
import { Alert } from "./Alert";
import Loading from "../../components/Loading";

const filterAlertsByRegions = (alerts, selectedRegions) => {
    if (selectedRegions.length === 0) return alerts;

    return alerts.reduce((filteredAlerts, alert) => {
        // Filter regions for each alert
        const filteredRegions = alert.regions.filter(region => selectedRegions.includes(region.region));
        
        // If there are any regions left after filtering, add the alert to the result
        if (filteredRegions.length > 0) {
            // Create a copy of the alert with the filtered regions
            const filteredAlert = {
                ...alert,
                regions: filteredRegions
            };
            filteredAlerts.push(filteredAlert);
        }
        return filteredAlerts;
    }, []);
};

export const Alerts = () => {
    const [selectedRegions, setSelectedRegions] = useState([]);

    const forecasts = useForecasts();
    const regions = useRegions();

    const forecastsLoaded = forecasts.every(query => query.isSuccess);

    const allForecasts = forecasts?.map((query) => query?.data).flatMap(query => query?.forecasts);
    const allGroupedAlerts = groupAlerts(allForecasts, regions.data?.regions);
    const filteredAlerts = filterAlertsByRegions(allGroupedAlerts, selectedRegions);

    const alertCount = filteredAlerts.length;

    const affectedZones = filteredAlerts.reduce((totalCount, alert) => {
        if (!alert.regions) return totalCount;
        let alertZoneCount = alert.regions.reduce((count, region) => count + region.zones.length, 0);
        return totalCount + alertZoneCount;
    }, 0);

    const isLoading = (forecasts.some(query => query.isLoading) || regions.isLoading);

    if (isLoading) {
        return (
            <Loading />
        );
    }

    return (
        <>
            <Box bg="white" mb={2} pt={5} px={4}>
                <Flex justifyContent='space-between' alignItems='center'>
                    <Text fontSize={20} fontWeight={700} color="gray.700">
                        Alerts
                        {' '}
                        {forecastsLoaded &&
                            <>
                                ({alertCount})
                            </>
                        }
                    </Text>
                    <RegionFilterDrawer regions={regions.data?.regions} selectedRegions={selectedRegions} setSelectedRegions={setSelectedRegions} />
                </Flex>
                <Text textAlign="left" color="gray.700" pb={2}>
                    {affectedZones} Zones Affected
                </Text>
                <Flex justifyContent={'flex-start'} overflowX={'auto'}>
                    <SelectedRegionBar regions={regions.data?.regions} selectedRegions={selectedRegions} setSelectedRegions={setSelectedRegions} />
                </Flex>
            </Box>
            {filteredAlerts.length > 0
                ? (
                    <Box bg="gray.50" p={4}>
                        <Card bg="white">
                            {filteredAlerts.map((alert, index) => (
                                <Alert alert={alert} regionData={regions.data?.regions} key={index} forecasts={forecasts} />
                            ))}
                        </Card>
                    </Box>
                ) : (
                    <Box p={4} mt={10} pb={10} textAlign='center'>
                      <Icon as={FiInfo} boxSize={'24px'} color='gray.500' />
                        <Text color='gray.500' fontWeight={600} fontSize={20}>No Alerts Found</Text>
                        <Text color='gray.500' fontSize={14} px={10}>
                            There are currently no alerts
                            {selectedRegions.length === 1 && (' in the selected region')}
                            {selectedRegions.length > 1 && (' in the selected regions')}
                            .
                        </Text>
                    </Box>
                )
            }
        </>
    );
};
